<template>
  <div>
    <div class="d-flex justify-content-between align-items-center mb-4 mt-5">
      <div class="d-flex align-items-center">
        <h2>Users</h2>
        <button @click="addUser" class="btn btn-success ml-4">
          Add User
        </button>
      </div>
      <div style="width: 50%;">
        <SearchBox
          v-model="searchTerm"
          placeholder="Search users..."
        />
      </div>
    </div>
    <AIVTable
      v-if="filteredUsers && filteredUsers.length"
      :columns="userColumns"
      :data="filteredUsers"
      :pagination="{ total: 0, totalPages: 0, currentPage: 0, perPage: 0 }"
    >
      <template #cell-dates="{ row }">
        <div class="dates-column">
          <div class="created-date">Created: {{ formatDate(row.createdAt) }}</div>
          <div class="updated-date">Updated: {{ formatDate(row.updatedAt) }}</div>
        </div>
      </template>
      <template #cell-actions="{ row }">
        <button @click="editUser(row)" class="btn btn-primary btn-sm mr-2">
          Edit
        </button>
        <button @click="removeUser(row)" class="btn btn-danger btn-sm">
          Delete
        </button>
      </template>
    </AIVTable>

    <!-- User Modal -->
    <AIVDialog
      :visible="$modal.state.userModal"
      :title="modalTitle"
      :show-footer="true"
      :custom-class="'user-modal'"
      @update:visible="$modal.close('userModal')"
    >
      <!-- Edit/Create Mode -->
      <div v-if="modal.mode === 'edit' || modal.mode === 'create'" class="user-form">
        <div class="form-group mb-3">
          <label for="userEmail" class="form-label">Email *</label>
          <input
            id="userEmail"
            v-model="modal.formData.email"
            type="email"
            class="form-control"
            placeholder="Enter email address"
            required
          />
        </div>
        <div class="form-group mb-3">
          <label for="userPassword" class="form-label">
            Password {{ modal.mode === 'edit' ? '(leave empty to keep current)' : '*' }}
          </label>
          <input
            id="userPassword"
            v-model="modal.formData.password"
            type="password"
            class="form-control"
            :placeholder="modal.mode === 'edit' ? 'Leave empty to keep current password' : 'Enter password'"
            :required="modal.mode === 'create'"
          />
          <small class="form-text text-muted">
            {{ modal.mode === 'edit' ? 'Only enter a password if you want to change it' : 'Password will be securely hashed when saved' }}
          </small>
        </div>
      </div>

      <!-- Remove Mode -->
      <div v-if="modal.mode === 'remove'" class="user-remove-confirm">
        <p class="text-center mb-4">
          Are you sure you want to delete the user 
          <strong>"{{ modal.selectedUser?.email }}"</strong>?
        </p>
        <p class="text-muted text-center">This action cannot be undone.</p>
      </div>

      <!-- Modal Footer -->
      <template #footer>
        <div class="d-flex justify-content-around">
          <button 
            @click="closeModal" 
            class="btn btn-secondary"
          >
            Cancel
          </button>
          <button 
            v-if="modal.mode === 'edit'"
            @click="saveUser" 
            class="btn btn-primary ml-2"
            :disabled="modal.loading || !modal.formData.email || !isValidEmail(modal.formData.email)"
          >
            {{ modal.loading ? 'Saving...' : 'Save' }}
          </button>
          <button 
            v-if="modal.mode === 'create'"
            @click="createUser" 
            class="btn btn-success ml-2"
            :disabled="modal.loading || !modal.formData.email || !modal.formData.password || !isValidEmail(modal.formData.email)"
          >
            {{ modal.loading ? 'Creating...' : 'Create User' }}
          </button>
          <button 
            v-if="modal.mode === 'remove'"
            @click="confirmRemoval" 
            class="btn btn-danger ml-2"
            :disabled="modal.loading"
          >
            {{ modal.loading ? 'Deleting...' : 'Delete' }}
          </button>
        </div>
      </template>
    </AIVDialog>

  </div>
</template>

<script>
import UsersAPIService from "@/serviceHandlers/UsersAPIservice";
import AIVTable from "@/components/AIVTable.vue";
import AIVDialog from "@/components/AIVDialog.vue";
import SearchBox from "@/components/SearchBox.vue";

const usersAPIService = new UsersAPIService()

export default {
  name: "UsersPage",
  components: {
    AIVTable,
    AIVDialog,
    SearchBox
  },
  data(){
    return {
      searchTerm: '',
      users: [],
      userColumns: [
        { key: 'email', label: 'Email' },
        { key: 'dates', label: 'Created / Updated', component: 'dates' },
        { 
          key: 'actions', 
          label: 'Actions', 
          sortable: false,
          component: 'div'
        }
      ],
      modal: {
        mode: null, // 'edit', 'remove', or 'create'
        selectedUser: null,
        loading: false,
        formData: {
          email: '',
          password: ''
        }
      }
    }
  },
  computed: {
    filteredUsers() {
      if (!this.users || !this.searchTerm) {
        return this.users || [];
      }
      
      const searchLower = this.searchTerm.toLowerCase();
      return this.users.filter(user => {
        const matchesEmail = user.email?.toLowerCase().includes(searchLower);
        
        return matchesEmail;
      });
    },
    modalTitle() {
      if (this.modal.mode === 'edit') {
        return 'Edit User';
      } else if (this.modal.mode === 'remove') {
        return 'Delete User';
      } else if (this.modal.mode === 'create') {
        return 'Create New User';
      }
      return '';
    }
  },
  async mounted(){
    console.clear()
    await this.getUsers()
  },
  methods: {
    async getUsers(){
      try {
        this.users = await usersAPIService.getUsers(this.$router)
        console.log(this.users)
      } catch (e) {
        console.error(e)
      }
    },
    editUser(user) {
      console.log(user)
      this.modal.selectedUser = user;
      this.modal.mode = 'edit';
      this.modal.formData.email = user.email;
      this.modal.formData.password = ''; // Always empty for security
      this.$modal.open('userModal');
    },
    removeUser(user) {
      this.modal.selectedUser = user;
      this.modal.mode = 'remove';
      this.$modal.open('userModal');
    },
    addUser() {
      this.modal.mode = 'create';
      this.modal.selectedUser = null;
      this.modal.formData.email = '';
      this.modal.formData.password = '';
      this.$modal.open('userModal');
    },
    closeModal() {
      this.$modal.close('userModal');
      this.modal.mode = null;
      this.modal.selectedUser = null;
      this.modal.loading = false;
      this.modal.formData.email = '';
      this.modal.formData.password = '';
    },
    async saveUser() {
      this.modal.loading = true;
      try {
        const userData = {
          email: this.modal.formData.email
        };
        
        // Only include password if it's provided
        if (this.modal.formData.password && this.modal.formData.password.trim()) {
          userData.password = this.modal.formData.password;
        }
        
        await usersAPIService.updateUser(
          this.modal.selectedUser.id,
          userData,
          this.$router
        );
        
        // Refresh the users data to show updated info
        await this.getUsers();
        this.closeModal();
        
        console.log('User updated successfully');
      } catch (error) {
        console.error('Error updating user:', error);
        if (error.response && error.response.status === 409) {
          alert('Email address already exists. Please use a different email.');
        } else {
          alert('Failed to update user. Please try again.');
        }
      } finally {
        this.modal.loading = false;
      }
    },
    async createUser() {
      this.modal.loading = true;
      try {
        const userData = {
          email: this.modal.formData.email,
          password: this.modal.formData.password
        };
        
        await usersAPIService.createUser(
          userData,
          this.$router
        );
        
        // Refresh the users data to show the new user
        await this.getUsers();
        this.closeModal();
        
        console.log('User created successfully');
      } catch (error) {
        console.error('Error creating user:', error);
        if (error.response && error.response.status === 409) {
          alert('Email address already exists. Please use a different email.');
        } else {
          alert('Failed to create user. Please try again.');
        }
      } finally {
        this.modal.loading = false;
      }
    },
    async confirmRemoval() {
      this.modal.loading = true;
      try {
        await usersAPIService.deleteUser(
          this.modal.selectedUser.id,
          this.$router
        );
        
        // Refresh the users data to remove the user from the list
        await this.getUsers();
        this.closeModal();
        
        console.log('User deleted successfully');
      } catch (error) {
        console.error('Error deleting user:', error);
        alert('Failed to delete user. Please try again.');
      } finally {
        this.modal.loading = false;
      }
    },
    formatDate(dateString) {
      if (!dateString) return 'N/A';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    },
    isValidEmail(email) {
      const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return emailPattern.test(email);
    }
  }
};
</script>

<style scoped>
.dates-column {
  font-size: 0.875rem;
}

.created-date {
  margin-bottom: 2px;
}

.updated-date {
  color: #6c757d;
  font-size: 0.8rem;
}
</style>